/**
 * terrain-environment.ts — derive global runtime environment from a
 * loaded terrain preset's `terrain_meta.json`.
 *
 * Three properties are surfaced:
 *   - `water_type` ("lake" | "sea") — drives the water palette tint.
 *   - `wind_exposure` (0..1)         — global wind attenuation factor.
 *   - `max_wave_amp_scale`           — cap on FFT amplitudeScale.
 *
 * Plus an optional `water_colors` override (sRGB hex) that wins over the
 * `water_type` preset.
 *
 * Side effects:
 *   - Mutates `state.windExposure / maxWaveAmpScale / waterType`.
 *   - Mutates the palette's WATER_SHALLOW/MID/DEEP slots and re-uploads.
 */

import type { AppState } from "../State";
import type { TerrainMetaJson } from "../assets/TerrainAsset";
import {
  PaletteIndex,
  type PaletteUniforms,
} from "../rendering/PaletteUniforms";
import { srgbHexToLinear } from "../rendering/LightingUniforms";

interface WaterPalette {
  shallow: string;
  mid: string;
  deep: string;
}

interface GroundPalette {
  grass: string;
  cliff: string;
  beach: string;
  rock: string;
}

/** Default per-water-type palettes (sRGB hex). */
const WATER_PALETTES: Record<"lake" | "sea", WaterPalette> = {
  // Lakes — slightly greenish slate (default palette tuned for Mazury).
  lake: {
    shallow: "#7AA0B8",
    mid: "#3F627A",
    deep: "#1F3447",
  },
  // Sea — more saturated turquoise-blue, deeper navy.
  sea: {
    shallow: "#5FB4C4",
    mid: "#2A7090",
    deep: "#0F2A44",
  },
};

/** Default per-ground-type terrain palettes (sRGB hex). */
const GROUND_PALETTES: Record<"earthy" | "rocky", GroundPalette> = {
  earthy: {
    grass: "#5A8B47",
    cliff: "#826E5A",
    beach: "#D4B896",
    rock: "#6B6157",
  },
  rocky: {
    grass: "#7B8A67",
    cliff: "#B6AF9D",
    beach: "#D9D1B8",
    rock: "#9C998D",
  },
};

/**
 * Apply terrain-derived environment to the running simulation. Idempotent:
 * safe to call again if the terrain meta ever changes at runtime.
 */
export function applyTerrainEnvironment(
  meta: TerrainMetaJson,
  state: AppState,
  palette: PaletteUniforms,
): void {
  // --- Wind & wave caps ---------------------------------------------------
  const exposure = clamp01(meta.wind_exposure ?? 1.0);
  const maxAmp = Math.max(0.0, meta.max_wave_amp_scale ?? 2.0);
  const waterType: "lake" | "sea" = meta.water_type ?? "lake";

  state.windExposure = exposure;
  state.maxWaveAmpScale = maxAmp;
  state.waterType = waterType;

  // --- Water palette ------------------------------------------------------
  const preset = WATER_PALETTES[waterType];
  const colors = {
    shallow: meta.water_colors?.shallow ?? preset.shallow,
    mid: meta.water_colors?.mid ?? preset.mid,
    deep: meta.water_colors?.deep ?? preset.deep,
  };
  palette.state.colors[PaletteIndex.WaterShallow] = srgbHexToLinear(
    colors.shallow,
  );
  palette.state.colors[PaletteIndex.WaterMid] = srgbHexToLinear(colors.mid);
  palette.state.colors[PaletteIndex.WaterDeep] = srgbHexToLinear(colors.deep);

  // --- Ground palette -----------------------------------------------------
  const groundType = meta.terrain_ground_type ?? "earthy";
  const groundPreset = GROUND_PALETTES[groundType];
  const groundColors = {
    grass: meta.terrain_ground_colors?.grass ?? groundPreset.grass,
    cliff: meta.terrain_ground_colors?.cliff ?? groundPreset.cliff,
    beach: meta.terrain_ground_colors?.beach ?? groundPreset.beach,
    rock: meta.terrain_ground_colors?.rock ?? groundPreset.rock,
  };
  palette.state.colors[PaletteIndex.LandGrass] = srgbHexToLinear(
    groundColors.grass,
  );
  palette.state.colors[PaletteIndex.LandCliff] = srgbHexToLinear(
    groundColors.cliff,
  );
  palette.state.colors[PaletteIndex.LandBeach] = srgbHexToLinear(
    groundColors.beach,
  );
  palette.state.colors[PaletteIndex.LandRock] = srgbHexToLinear(
    groundColors.rock,
  );
  palette.upload();
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}
